import {
  ArrowLeft,
  ArrowRight,
  Bell,
  Check,
  ChevronLeft,
  ChevronRight,
  Expand,
  Fingerprint,
  GripVertical,
  LayoutGrid,
  LoaderCircle,
  Minus,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useAppState } from "../../app-state";
import { InscriptionDocumentFrame } from "../../components/InscriptionDocumentFrame";
import { PixelArtImage } from "../../components/PixelArtImage";
import { ResilientInscriptionMedia } from "../../components/ResilientInscriptionMedia";
import {
  fetchCreatorOwnedInscriptions,
  updateCreatorProfile,
} from "../../lib/api";
import {
  artefactApi,
  type ArchiveWork,
  type ArtefactGallery,
  type ArtworkPassport,
  type ArtInbox,
  type GalleryInput,
} from "../../lib/artefact-community";
import type { AuctionRecord } from "../../lib/auctions";
import type { CreatorOwnedInscriptionPage } from "../../lib/launchpad";
import {
  auctionDateTimeInputValue,
  parseAuctionDateTimeInput,
} from "../auctions/auction-date";
import "./community.css";

const failure = (e: unknown) =>
  e instanceof Error ? e.message : "Please try again";
const shortDate = (n: number) =>
  new Date(n).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
const blankWork = (): ArchiveWork => ({
  inscriptionId: "",
  title: "",
  year: "",
  series: "",
  statement: "",
  edition: "",
});
const blankGallery = (): GalleryInput => ({
  kind: "exhibition",
  title: "",
  description: "",
  process: "",
  curator: "",
  opensAt: null,
  theme: "ink",
  published: false,
  anonymous: true,
  works: [],
});
function ErrorMessage({ text }: { text: string }) {
  return text ? (
    <p className="ar-community-error" role="alert">
      {text}
    </p>
  ) : null;
}
function Verify() {
  const { wallet, authorizeWallet, setWalletModalOpen } = useAppState();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="ar-empty">
      <Fingerprint />
      <h2>Your space, by invitation.</h2>
      <p>
        Connect and verify your wallet to edit your archive or save private
        preferences. Viewing art never requires a wallet.
      </p>
      <button
        className="ar-primary"
        disabled={busy}
        onClick={() => {
          void (async () => {
            if (!wallet) return setWalletModalOpen(true);
            setBusy(true);
            try {
              await authorizeWallet(true);
            } catch (e) {
              setError(failure(e));
            } finally {
              setBusy(false);
            }
          })();
        }}
      >
        {busy ? "Verifying…" : wallet ? "Verify wallet" : "Connect wallet"}
      </button>
      <ErrorMessage text={error} />
    </div>
  );
}

/** Original media, not a static thumbnail. Interactive inscriptions remain on an isolated origin. */
function OriginalMedia({
  id,
  title,
  zoom,
}: {
  id: string;
  title: string;
  zoom: number;
}) {
  const [passport, setPassport] = useState<ArtworkPassport | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    setPassport(null);
    setError("");
    artefactApi<{ passport: ArtworkPassport }>(`/passports/${id}`)
      .then((d) => {
        if (alive) setPassport(d.passport);
      })
      .catch((e) => {
        if (alive) setError(failure(e));
      });
    return () => {
      alive = false;
    };
  }, [id]);
  const source = `https://ordinals.com/content/${encodeURIComponent(id)}`;
  if (!passport && !error)
    return <LoaderCircle className="spin" aria-label="Loading original" />;
  const mime = passport?.contentType ?? "";
  return (
    <div
      className="ar-original"
      style={{ width: `${zoom * 100}%`, height: `${zoom * 100}%` }}
    >
      {mime.startsWith("image/") ? (
        <PixelArtImage
          src={source}
          alt={title}
          onError={() =>
            setError(
              "Original image could not load. Open the on-chain original below.",
            )
          }
        />
      ) : mime.startsWith("video/") ? (
        <video controls playsInline src={source} aria-label={title} />
      ) : mime.startsWith("audio/") ? (
        <audio controls src={source} aria-label={title} />
      ) : (
        <InscriptionDocumentFrame
          source={{ inscriptionId: id }}
          title={title}
          loading="eager"
        />
      )}
      {error ? (
        <p role="status">
          {error}{" "}
          <a href={source} target="_blank" rel="noopener noreferrer">
            Open original
          </a>
        </p>
      ) : null}
    </div>
  );
}

export function GalleryViewer({
  works,
  initial = 0,
  onClose,
}: {
  works: ArchiveWork[];
  initial?: number;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [index, setIndex] = useState(initial);
  const [zoom, setZoom] = useState(1);
  const work = works[index];
  const move = useCallback(
    (step: number) => {
      setIndex((i) => (i + step + works.length) % works.length);
      setZoom(1);
    },
    [works.length],
  );
  useEffect(() => {
    const node = dialog.current;
    const trigger =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    node?.showModal();
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
      node?.close();
      trigger?.focus();
    };
  }, []);
  if (!work) return null;
  return (
    <dialog
      ref={dialog}
      className="ar-viewer"
      aria-label="Fullscreen artwork gallery"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onKeyDown={(e) => {
        if (
          (e.target as HTMLElement).closest("input,textarea,iframe,video,audio")
        )
          return;
        if (e.key === "ArrowRight") {
          e.preventDefault();
          move(1);
        }
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          move(-1);
        }
      }}
    >
      <header>
        <span>
          {index + 1} / {works.length}
        </span>
        <div>
          <button
            className="ar-icon"
            aria-label="Zoom out"
            disabled={zoom <= 1}
            onClick={() => setZoom((z) => Math.max(1, z - 0.25))}
          >
            <Minus />
          </button>
          <button onClick={() => setZoom(1)}>{Math.round(zoom * 100)}%</button>
          <button
            className="ar-icon"
            aria-label="Zoom in"
            disabled={zoom >= 3}
            onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
          >
            <Plus />
          </button>
          <button
            className="ar-icon"
            aria-label="Close gallery"
            onClick={onClose}
          >
            <X />
          </button>
        </div>
      </header>
      <div className="ar-viewer-stage">
        <OriginalMedia
          key={work.inscriptionId}
          id={work.inscriptionId}
          title={work.title}
          zoom={zoom}
        />
      </div>
      <footer>
        <button
          className="ar-icon"
          aria-label="Previous artwork"
          disabled={works.length < 2}
          onClick={() => move(-1)}
        >
          <ChevronLeft />
        </button>
        <div>
          <h2>{work.title}</h2>
          <p>
            {[work.year, work.series, work.edition]
              .filter(Boolean)
              .join(" · ") || "Bitcoin original"}
          </p>
          <a
            href={`https://ordinals.com/inscription/${work.inscriptionId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            On-chain record
          </a>
        </div>
        <button
          className="ar-icon"
          aria-label="Next artwork"
          disabled={works.length < 2}
          onClick={() => move(1)}
        >
          <ChevronRight />
        </button>
      </footer>
    </dialog>
  );
}
export function AuctionArtActions({ auction }: { auction: AuctionRecord }) {
  const [view, setView] = useState(false);
  return (
    <>
      <div className="ar-art-actions">
        <button className="ar-secondary" onClick={() => setView(true)}>
          <Expand /> Gallery mode
        </button>
        <Link
          className="ar-secondary"
          to={`/artworks/${auction.inscriptionId}`}
        >
          <Fingerprint /> Artwork passport
        </Link>
      </div>
      <WatchAuction auction={auction} />
      {view ? (
        <GalleryViewer
          works={[
            {
              ...blankWork(),
              inscriptionId: auction.inscriptionId,
              title: auction.title,
            },
          ]}
          onClose={() => setView(false)}
        />
      ) : null}
    </>
  );
}

export function GalleryCatalogue({ mine = false }: { mine?: boolean }) {
  const { authorization } = useAppState();
  const [params, setParams] = useSearchParams();
  const kind = params.get("kind") ?? "all";
  const [page, setPage] = useState(0);
  const [data, setData] = useState<{
    galleries: ArtefactGallery[];
    more: boolean;
  } | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (mine && !authorization) return;
    let alive = true;
    setData(null);
    setError("");
    artefactApi<{ galleries: ArtefactGallery[]; more: boolean }>(
      `/galleries?kind=${encodeURIComponent(kind)}&page=${page}&mine=${mine}`,
      authorization,
    )
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        if (alive) setError(failure(e));
      });
    return () => {
      alive = false;
    };
  }, [kind, page, mine, authorization]);
  return (
    <main className="ar-page ar-community">
      <header className="ar-community-heading">
        <div>
          <p className="ar-eyebrow">
            {mine ? "Your publishing studio" : "A considered point of view"}
          </p>
          <h1>
            {mine ? (
              <>
                Your <em>galleries.</em>
              </>
            ) : (
              <>
                Art in <em>context.</em>
              </>
            )}
          </h1>
          <p>
            {mine
              ? "Draft in private. Publish only the works and identity you choose."
              : "Artist archives, independent exhibitions and personal collections. Chosen by people, not an algorithm."}
          </p>
        </div>
        <Link className="ar-primary" to="/galleries/new">
          Create a gallery <Plus />
        </Link>
      </header>
      <nav className="ar-community-tabs" aria-label="Gallery type">
        {["all", "artist", "exhibition", "collector"].map((k) => (
          <button
            key={k}
            aria-pressed={kind === k}
            onClick={() => {
              setPage(0);
              setParams(k === "all" ? {} : { kind: k });
            }}
          >
            {
              {
                all: "All spaces",
                artist: "Artist archives",
                exhibition: "Exhibitions",
                collector: "Collector galleries",
              }[k]
            }
          </button>
        ))}
        <Link to={mine ? "/exhibitions" : "/my-galleries"}>
          {mine ? "Public spaces" : "My galleries"}
        </Link>
      </nav>
      {mine && !authorization ? (
        <Verify />
      ) : (
        <>
          <ErrorMessage text={error} />
          {!data && !error ? <p role="status">Loading the archive…</p> : null}
          <div className="ar-gallery-list">
            {data?.galleries.map((g) => (
              <Link
                key={g.id}
                to={mine ? `/galleries/${g.id}/edit` : `/galleries/${g.id}`}
                className={`ar-gallery-cover theme-${g.theme}`}
              >
                <div className="ar-cover-art">
                  {g.works[0] ? (
                    <ResilientInscriptionMedia
                      inscriptionId={g.works[0].inscriptionId}
                      label={g.title}
                      className="ar-media"
                    />
                  ) : (
                    <LayoutGrid />
                  )}
                </div>
                <div>
                  <p className="ar-eyebrow">
                    {g.kind === "artist"
                      ? "Artist archive"
                      : g.kind === "collector"
                        ? "Personal collection"
                        : "Independent exhibition"}
                    {mine && !g.published ? " · Draft" : ""}
                  </p>
                  <h2>{g.title}</h2>
                  <p>
                    {g.curatorName} · {g.works.length} works
                    {g.opensAt ? ` · Opens ${shortDate(g.opensAt)}` : ""}
                  </p>
                  <span>
                    Enter the space <ArrowRight />
                  </span>
                </div>
              </Link>
            ))}
          </div>
          {data && !data.galleries.length ? (
            <section className="ar-community-empty">
              <span>01 — YOUR NEXT DISCOVERY</span>
              <h2>A space waiting for a point of view.</h2>
              <p>
                {mine
                  ? "Make an artist archive, curate an exhibition or share a few works from your collection. Nothing is published by default."
                  : "No galleries have been published here yet. Create the first, or discover the live auction room."}
              </p>
              <Link
                className="ar-text-link"
                to={mine ? "/galleries/new" : "/auctions"}
              >
                {mine ? "Start a gallery" : "Explore auctions"} <ArrowRight />
              </Link>
            </section>
          ) : null}
          <div className="ar-pagination">
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              Previous
            </button>
            <span>Page {page + 1}</span>
            <button
              disabled={!data?.more}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </main>
  );
}

export function GalleryPage() {
  const { galleryId = "" } = useParams();
  const { authorization } = useAppState();
  const [data, setData] = useState<{
    gallery: ArtefactGallery;
    canEdit: boolean;
  } | null>(null);
  const [error, setError] = useState("");
  const [viewer, setViewer] = useState<number | null>(null);
  const [series, setSeries] = useState("");
  const [chronological, setChronological] = useState(false);
  useEffect(() => {
    let alive = true;
    setData(null);
    setError("");
    artefactApi<{ gallery: ArtefactGallery; canEdit: boolean }>(
      `/galleries/${galleryId}`,
      authorization,
    )
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        if (alive) setError(failure(e));
      });
    return () => {
      alive = false;
    };
  }, [galleryId, authorization]);
  if (!data)
    return (
      <main className="ar-page">
        <ErrorMessage text={error} />
        {!error ? "Opening the gallery…" : null}
      </main>
    );
  const g = data.gallery;
  const works = g.works
    .filter((w) => !series || w.series === series)
    .slice()
    .sort((a, b) => (chronological ? a.year.localeCompare(b.year) : 0));
  return (
    <main className={`ar-community ar-gallery-page theme-${g.theme}`}>
      <header className="ar-gallery-intro">
        <Link className="ar-back" to="/exhibitions">
          <ArrowLeft /> All spaces
        </Link>
        <p className="ar-eyebrow">
          {g.kind === "artist"
            ? "Artist archive"
            : g.kind === "collector"
              ? "Personal collection"
              : "Independently curated exhibition"}
          {!g.published ? " · Private draft" : ""}
        </p>
        <h1>{g.title}</h1>
        <p className="ar-gallery-description">{g.description}</p>
        <div className="ar-gallery-byline">
          <span>
            {g.curatorName}
            {g.curator && g.curator !== g.curatorName ? ` · ${g.curator}` : ""}
          </span>
          {g.curatorUsername ? (
            <FollowArtist username={g.curatorUsername} />
          ) : null}
          {data.canEdit ? (
            <Link to={`/galleries/${g.id}/edit`}>Edit this space</Link>
          ) : null}
        </div>
        {g.kind === "artist" && g.curatorBio ? (
          <p className="ar-gallery-description">{g.curatorBio}</p>
        ) : null}
        {g.opensAt ? (
          <p>
            {g.opensAt > Date.now() ? "Opens" : "Opened"}{" "}
            {new Date(g.opensAt).toLocaleString("en-GB")} · Preview visible now
          </p>
        ) : null}
        <div className="ar-art-actions">
          <button
            className="ar-primary"
            disabled={!works.length}
            onClick={() => setViewer(0)}
          >
            <Expand /> Enter gallery mode
          </button>
          <a className="ar-secondary" href="#catalogue">
            The works <ArrowRight />
          </a>
        </div>
      </header>
      {g.process ? (
        <section className="ar-curator-note">
          <p className="ar-eyebrow">
            {g.kind === "artist"
              ? "Inside the practice"
              : "A note from the curator"}
          </p>
          <p>{g.process}</p>
        </section>
      ) : null}
      <section className="ar-gallery-works" id="catalogue">
        <header>
          <h2>
            The works <em>({works.length})</em>
          </h2>
          <div>
            <label>
              Series{" "}
              <select
                value={series}
                onChange={(e) => setSeries(e.target.value)}
              >
                <option value="">All series</option>
                {[...new Set(g.works.map((w) => w.series).filter(Boolean))].map(
                  (s) => (
                    <option key={s}>{s}</option>
                  ),
                )}
              </select>
            </label>
            {g.kind === "artist" ? (
              <label>
                <input
                  type="checkbox"
                  checked={chronological}
                  onChange={(e) => setChronological(e.target.checked)}
                />{" "}
                Chronological
              </label>
            ) : null}
          </div>
        </header>
        <div className="ar-work-grid">
          {works.map((w, i) => (
            <article key={w.inscriptionId}>
              <button
                className="ar-work-image"
                onClick={() => setViewer(i)}
                aria-label={`View ${w.title} in gallery mode`}
              >
                <ResilientInscriptionMedia
                  inscriptionId={w.inscriptionId}
                  label={w.title}
                  className="ar-media"
                />
                <span>
                  <Expand /> View original
                </span>
              </button>
              <p className="ar-eyebrow">
                {w.series || "Bitcoin original"}
                {w.year ? ` · ${w.year}` : ""}
              </p>
              <h3>{w.title}</h3>
              {w.edition ? (
                <p>
                  {w.edition} <small>· curator-provided</small>
                </p>
              ) : null}
              {w.statement ? <p>{w.statement}</p> : null}
              <Link
                className="ar-text-link"
                to={`/artworks/${w.inscriptionId}?gallery=${g.id}`}
              >
                Artwork passport <Fingerprint />
              </Link>
            </article>
          ))}
        </div>
      </section>
      <section className="ar-curator-note">
        <Fingerprint />
        <p>
          {g.kind === "collector"
            ? `Ownership checked when this selection was published${g.ownershipCheckedAt ? ` on ${shortDate(g.ownershipCheckedAt)}` : ""}. This is a snapshot, not a live ownership guarantee. `
            : "Works, artist statements, editions and series are curator-provided; wallet control is not proof of authorship. "}
          This website’s text and arrangement are editable. The inscription
          records remain on Bitcoin.
        </p>
      </section>
      <CatalogueGuide works={g.works} />
      {viewer !== null ? (
        <GalleryViewer
          works={works}
          initial={viewer}
          onClose={() => setViewer(null)}
        />
      ) : null}
    </main>
  );
}

export function CatalogueGuide({ works }: { works?: ArchiveWork[] }) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const galleryInput = JSON.stringify((works ?? []).map(w => ({
    inscriptionId: w.inscriptionId, title: w.title,
    traits: {
      ...(w.year ? { Year: w.year } : {}),
      ...(w.series ? { Series: w.series } : {}),
      ...(w.edition ? { Edition: w.edition } : {}),
    },
  })), null, 2);
  return (
    <section className="ar-catalogue-guide">
      <div>
        <p className="ar-eyebrow">Keep the exhibition, too</p>
        <h2>
          A catalogue.
          <br />
          <em>Beyond this website.</em>
        </h2>
        <p>
          A permanent record of the selection you made, built with existing
          Bitcoin inscriptions.
        </p>
      </div>
      <div>
        <ol>
          <li>
            <b>01</b>
            <span>
              Select your final inscription IDs, names and notes. Confirm that
              you have the rights to publish the accompanying material.
            </span>
          </li>
          <li>
            <b>02</b>
            <span>
              Open the gallery inscription builder. Paste the IDs or gallery
              JSON, choose a cover, add metadata and preview. A gallery
              references the works; it does not move them.
            </span>
          </li>
          <li>
            <b>03</b>
            <span>
              Review the network cost, service price and destination, then sign
              in your wallet. For parent–child provenance, use the builder’s
              parent controls with an inscription you own.
            </span>
          </li>
          <li>
            <b>04</b>
            <span>
              Keep the confirmed inscription ID as your catalogue’s permanent
              record. Website edits will not update it; a revised catalogue
              needs another inscription.
            </span>
          </li>
        </ol>
        <div className="ar-art-actions">
          <Link
            className="ar-primary"
            to="/inscribe/gallery"
            state={works?.length ? { galleryInput } : undefined}
          >
            Inscribe a gallery
          </Link>
          {works?.length ? (
            <button
              className="ar-secondary"
              onClick={() => {
                void (async () => {
                  try {
                    await navigator.clipboard.writeText(
                      galleryInput,
                    );
                    setCopied(true);
                    setError("");
                  } catch {
                    setError(
                      "Clipboard unavailable. Copy the inscription IDs from the artwork passports.",
                    );
                  }
                })();
              }}
            >
              {copied ? (
                <>
                  <Check /> Gallery JSON copied
                </>
              ) : (
                "Copy gallery JSON"
              )}
            </button>
          ) : null}
        </div>
        <ErrorMessage text={error} />
        <small>
          The builder opens here on Artefact with this selection. Nothing is signed or sent by following this link.
        </small>
      </div>
    </section>
  );
}

function ProfileEditor() {
  const { creatorProfile, setCreatorProfile, authorizeWallet } = useAppState();
  const [username, setUsername] = useState(creatorProfile?.username ?? "");
  const [bio, setBio] = useState(creatorProfile?.bio ?? "");
  const [website, setWebsite] = useState(creatorProfile?.websiteUrl ?? "");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const token = await authorizeWallet(true);
      if (!token) throw new Error("Verify your wallet");
      setCreatorProfile(
        await updateCreatorProfile(token, {
          username,
          displayName: username,
          bio,
          websiteUrl: website,
          avatarInscriptionId: creatorProfile?.avatarInscriptionId ?? "",
          bannerInscriptionId: creatorProfile?.bannerInscriptionId ?? "",
          bannerImageKey: creatorProfile?.bannerImageKey ?? "",
          publicWallet: creatorProfile?.publicWallet ?? false,
        }),
      );
      setNotice("Profile saved. This updates your shared artist identity.");
    } catch (e) {
      setNotice(failure(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <details className="ar-profile-editor">
      <summary>
        Artist identity & biography{" "}
        {creatorProfile
          ? `· @${creatorProfile.username}`
          : "· create your profile first"}
      </summary>
      <form onSubmit={(event) => void save(event)}>
        <p>
          Your existing wallet profile is reused. No new account or public
          wallet disclosure is required.
        </p>
        <label>
          Username
          <input
            required
            minLength={2}
            maxLength={30}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            pattern="[a-zA-Z0-9_-]{2,30}"
          />
        </label>
        <label>
          Biography
          <textarea
            maxLength={240}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
          />
        </label>
        <label>
          Website
          <input
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            maxLength={200}
            placeholder="https://your-studio.com"
          />
        </label>
        <button className="ar-secondary" disabled={busy}>
          Save shared profile
        </button>
        <p role="status">{notice}</p>
      </form>
    </details>
  );
}
function InventoryPicker({ add }: { add: (work: ArchiveWork) => void }) {
  const { authorization } = useAppState();
  const [data, setData] = useState<CreatorOwnedInscriptionPage | null>(null);
  const [page, setPage] = useState(0);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!authorization) return;
    let alive = true;
    setError("");
    fetchCreatorOwnedInscriptions(authorization, page)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        if (alive) setError(failure(e));
      });
    return () => {
      alive = false;
    };
  }, [authorization, page, revision]);
  return (
    <details className="ar-inventory-picker">
      <summary>Add from my verified Taproot inventory</summary>
      <p>Private until you add selected works and explicitly publish.</p>
      <ErrorMessage text={error} />
      <div>
        {data?.items.map((w) => (
          <button
            key={w.inscriptionId}
            type="button"
            onClick={() =>
              add({
                ...blankWork(),
                inscriptionId: w.inscriptionId,
                title: w.name || `Inscription ${w.inscriptionId.slice(0, 8)}`,
              })
            }
          >
            <ResilientInscriptionMedia
              inscriptionId={w.inscriptionId}
              label={w.name || "Inscription"}
              className="ar-media"
            />
            <span>{w.name || w.inscriptionId.slice(0, 12)}</span>
            <Plus />
          </button>
        ))}
      </div>
      <p>
        {data?.refreshing
          ? "Inventory is indexing; refresh in a moment."
          : `${data?.total ?? 0} indexed works`}
      </p>
      <div className="ar-art-actions">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0}
        >
          Previous
        </button>
        <button type="button" onClick={() => setRevision((n) => n + 1)}>
          Refresh inventory
        </button>
        <button
          type="button"
          onClick={() => setPage((p) => p + 1)}
          disabled={!data || (page + 1) * data.pageSize >= data.total}
        >
          Next
        </button>
      </div>
    </details>
  );
}

export function GalleryEditor() {
  const { galleryId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { authorization, authorizeWallet } = useAppState();
  const [input, setInput] = useState<GalleryInput>(() => {
    const kind = params.get("kind");
    return kind === "artist" || kind === "collector"
      ? { ...blankGallery(), kind, anonymous: kind !== "artist" }
      : blankGallery();
  });
  const [revision, setRevision] = useState<number | null>(null);
  const [loading, setLoading] = useState(Boolean(galleryId));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(false);
  useEffect(() => {
    if (!galleryId || !authorization) return;
    let alive = true;
    setLoading(true);
    artefactApi<{
      gallery: ArtefactGallery;
      canEdit: boolean;
      draft?: ArtefactGallery;
    }>(`/galleries/${galleryId}`, authorization)
      .then((d) => {
        if (!alive) return;
        if (!d.canEdit)
          throw new Error("Only the curator can edit this gallery");
        setInput(d.draft ?? d.gallery);
        setRevision(d.gallery.revision);
      })
      .catch((e) => {
        if (alive) setError(failure(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [galleryId, authorization]);
  function patch(p: Partial<GalleryInput>) {
    setInput((i) => ({ ...i, ...p }));
  }
  function workPatch(index: number, p: Partial<ArchiveWork>) {
    setInput((i) => ({
      ...i,
      works: i.works.map((w, n) => (n === index ? { ...w, ...p } : w)),
    }));
  }
  function add(w: ArchiveWork) {
    if (input.works.length >= 60) return setError("Choose at most 60 works");
    if (
      w.inscriptionId &&
      input.works.some((a) => a.inscriptionId === w.inscriptionId)
    )
      return setError("That work is already selected");
    patch({ works: [...input.works, w] });
  }
  function move(index: number, delta: number) {
    const works = input.works.slice();
    const next = index + delta;
    if (next < 0 || next >= works.length) return;
    [works[index], works[next]] = [works[next]!, works[index]!];
    patch({ works });
  }
  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const token = await authorizeWallet(true);
      if (!token) throw new Error("Verify your wallet");
      const result = await artefactApi<{ id: string }>(
        "/galleries/save",
        token,
        { gallery: input, ...(galleryId ? { id: galleryId, revision } : {}) },
      );
      void navigate(`/galleries/${result.id}`);
    } catch (e) {
      setError(failure(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="ar-page ar-community">
      <Link className="ar-back" to="/my-galleries">
        <ArrowLeft /> My galleries
      </Link>
      <header className="ar-community-heading">
        <div>
          <p className="ar-eyebrow">A space of your own</p>
          <h1>
            {galleryId ? "Refine the" : "Set the"} <em>scene.</em>
          </h1>
          <p>
            Write the context. Choose the originals. Give the work room to
            breathe.
          </p>
        </div>
      </header>
      {!authorization ? (
        <Verify />
      ) : (
        <>
          <ProfileEditor />
          <ErrorMessage text={error} />
          {loading ? (
            <p>Loading your draft…</p>
          ) : (
            <form
              className="ar-gallery-editor"
              onSubmit={(event) => void save(event)}
            >
              <div className="ar-editor-fields">
                <label>
                  Type
                  <select
                    disabled={Boolean(galleryId)}
                    value={input.kind}
                    onChange={(e) =>
                      patch({
                        kind: e.target.value as GalleryInput["kind"],
                        anonymous: e.target.value !== "artist",
                      })
                    }
                  >
                    <option value="exhibition">Independent exhibition</option>
                    <option value="artist">
                      Artist archive (one per profile)
                    </option>
                    <option value="collector">Collector gallery</option>
                  </select>
                </label>
                <label>
                  Visual identity
                  <select
                    value={input.theme}
                    onChange={(e) =>
                      patch({ theme: e.target.value as GalleryInput["theme"] })
                    }
                  >
                    <option value="ink">Ink — dark exhibition room</option>
                    <option value="paper">Paper — quiet white gallery</option>
                    <option value="lavender">
                      Silver — soft grey gallery
                    </option>
                  </select>
                </label>
                <label className="full">
                  Title
                  <input
                    required
                    maxLength={120}
                    value={input.title}
                    onChange={(e) => patch({ title: e.target.value })}
                  />
                </label>
                <label className="full">
                  Introduction
                  <textarea
                    maxLength={4000}
                    rows={4}
                    value={input.description}
                    onChange={(e) => patch({ description: e.target.value })}
                  />
                </label>
                <label className="full">
                  {input.kind === "artist"
                    ? "Process notes / practice"
                    : "Curator’s note"}
                  <textarea
                    maxLength={6000}
                    rows={5}
                    value={input.process}
                    onChange={(e) => patch({ process: e.target.value })}
                  />
                </label>
                <label>
                  Curator credit
                  <input
                    maxLength={120}
                    value={input.curator}
                    onChange={(e) => patch({ curator: e.target.value })}
                  />
                </label>
                <label>
                  Opening (optional, your local time)
                  <input
                    type="datetime-local"
                    value={
                      input.opensAt === null
                        ? ""
                        : auctionDateTimeInputValue(input.opensAt)
                    }
                    onChange={(e) =>
                      patch({
                        opensAt: e.target.value
                          ? parseAuctionDateTimeInput(e.target.value)
                          : null,
                      })
                    }
                  />
                </label>
              </div>
              <section>
                <div className="ar-section-heading">
                  <h2>
                    The selection <em>{input.works.length} / 60</em>
                  </h2>
                  <button
                    type="button"
                    className="ar-secondary"
                    onClick={() => add(blankWork())}
                  >
                    <Plus /> Add inscription ID
                  </button>
                </div>
                <InventoryPicker add={add} />
                <p>
                  Use the up/down controls to arrange the display. Authorship
                  and editions are self-described; parent links are
                  independently shown in each passport.
                </p>
                {input.works.map((w, i) => (
                  <fieldset className="ar-work-editor" key={i}>
                    <legend>Work {i + 1}</legend>
                    <div className="ar-work-edit-toolbar">
                      <GripVertical />
                      <button
                        type="button"
                        aria-label={`Move work ${i + 1} earlier`}
                        disabled={i === 0}
                        onClick={() => move(i, -1)}
                      >
                        <ChevronLeft />
                      </button>
                      <button
                        type="button"
                        aria-label={`Move work ${i + 1} later`}
                        disabled={i === input.works.length - 1}
                        onClick={() => move(i, 1)}
                      >
                        <ChevronRight />
                      </button>
                      <button
                        type="button"
                        aria-label={`Remove work ${i + 1}`}
                        onClick={() =>
                          patch({
                            works: input.works.filter((_, n) => n !== i),
                          })
                        }
                      >
                        <Trash2 />
                      </button>
                    </div>
                    <div className="ar-editor-fields">
                      <label className="full">
                        Inscription ID
                        <input
                          required
                          pattern="[0-9a-f]{64}i[0-9]{1,10}"
                          value={w.inscriptionId}
                          onChange={(e) =>
                            workPatch(i, {
                              inscriptionId: e.target.value
                                .trim()
                                .toLowerCase(),
                            })
                          }
                        />
                      </label>
                      <label>
                        Title
                        <input
                          required
                          maxLength={120}
                          value={w.title}
                          onChange={(e) =>
                            workPatch(i, { title: e.target.value })
                          }
                        />
                      </label>
                      <label>
                        Year
                        <input
                          pattern="[0-9]{4}"
                          maxLength={4}
                          value={w.year}
                          onChange={(e) =>
                            workPatch(i, { year: e.target.value })
                          }
                        />
                      </label>
                      <label>
                        Series
                        <input
                          maxLength={100}
                          value={w.series}
                          onChange={(e) =>
                            workPatch(i, { series: e.target.value })
                          }
                        />
                      </label>
                      <label>
                        Edition (curator-provided)
                        <input
                          maxLength={100}
                          value={w.edition}
                          onChange={(e) =>
                            workPatch(i, { edition: e.target.value })
                          }
                          placeholder="1 / 1"
                        />
                      </label>
                      <label className="full">
                        Artist statement / notes
                        <textarea
                          maxLength={2000}
                          value={w.statement}
                          onChange={(e) =>
                            workPatch(i, { statement: e.target.value })
                          }
                        />
                      </label>
                    </div>
                  </fieldset>
                ))}
              </section>
              <section className="ar-publication-panel">
                <h2>Your publication choices.</h2>
                {input.kind !== "artist" ? (
                  <label>
                    <input
                      type="checkbox"
                      checked={input.anonymous}
                      onChange={(e) => patch({ anonymous: e.target.checked })}
                    />{" "}
                    Hide my profile and curator credit (pseudonymous gallery)
                  </label>
                ) : (
                  <p>
                    Your artist profile is credited publicly. Statements are not
                    independently verified.
                  </p>
                )}
                <label>
                  <input
                    type="checkbox"
                    checked={input.published}
                    onChange={(e) => patch({ published: e.target.checked })}
                  />{" "}
                  Publish these selected works and text publicly
                </label>
                <p>
                  Unchecked means a private draft. Publishing never reveals your
                  whole wallet. Bitcoin records may still link individual
                  inscriptions to public addresses; anonymity is not guaranteed.
                  Opening dates announce the show but do not hide previews.
                </p>
                {input.kind === "collector" ? (
                  <p>
                    Selected collector works are checked against linked-wallet
                    ownership on publication. Later transfers do not
                    automatically update this snapshot.
                  </p>
                ) : null}
                <div className="ar-art-actions">
                  <button
                    type="button"
                    className="ar-secondary"
                    disabled={
                      !input.works.length ||
                      input.works.some(
                        (w) => !/^[0-9a-f]{64}i\d{1,10}$/.test(w.inscriptionId),
                      )
                    }
                    onClick={() => setPreview(true)}
                  >
                    <Expand /> Preview originals
                  </button>
                  <button className="ar-primary" disabled={busy}>
                    {busy
                      ? "Checking & saving…"
                      : input.published
                        ? "Save & publish selection"
                        : "Save private draft"}
                  </button>
                </div>
              </section>
            </form>
          )}
          {preview ? (
            <GalleryViewer
              works={input.works}
              onClose={() => setPreview(false)}
            />
          ) : null}
        </>
      )}
    </main>
  );
}

export function PassportPage() {
  const { inscriptionId = "" } = useParams();
  const [params] = useSearchParams();
  const [passport, setPassport] = useState<ArtworkPassport | null>(null);
  const [context, setContext] = useState<ArchiveWork | null>(null);
  const [error, setError] = useState("");
  const [view, setView] = useState(false);
  useEffect(() => {
    let alive = true;
    setPassport(null);
    setError("");
    artefactApi<{ passport: ArtworkPassport }>(`/passports/${inscriptionId}`)
      .then((d) => {
        if (alive) setPassport(d.passport);
      })
      .catch((e) => {
        if (alive) setError(failure(e));
      });
    const gallery = params.get("gallery");
    setContext(null);
    if (gallery)
      artefactApi<{ gallery: ArtefactGallery }>(`/galleries/${gallery}`)
        .then((d) => {
          if (alive)
            setContext(
              d.gallery.works.find((w) => w.inscriptionId === inscriptionId) ??
                null,
            );
        })
        .catch(() => {});
    return () => {
      alive = false;
    };
  }, [inscriptionId, params]);
  const title =
    context?.title ||
    (passport?.number === null || passport?.number === undefined
      ? "Bitcoin original"
      : `Inscription ${passport.number.toLocaleString()}`);
  return (
    <main className="ar-page ar-community">
      <Link className="ar-back" to="/exhibitions">
        <ArrowLeft /> The archive
      </Link>
      <header className="ar-community-heading">
        <div>
          <p className="ar-eyebrow">Artwork passport</p>
          <h1>{title}</h1>
          <p>The object, its record, and the distinction between the two.</p>
        </div>
      </header>
      <ErrorMessage text={error} />
      {!passport && !error ? <p>Reading the inscription record…</p> : null}
      {passport ? (
        <div className="ar-passport">
          <div>
            <button
              className="ar-passport-art"
              onClick={() => setView(true)}
              aria-label="View original in fullscreen"
            >
              <ResilientInscriptionMedia
                inscriptionId={inscriptionId}
                label={title}
                className="ar-media"
              />
              <span>
                <Expand /> View original
              </span>
            </button>
            {context ? (
              <section>
                <p className="ar-eyebrow">Curator-provided context</p>
                <h2>{context.title}</h2>
                <p>
                  {[context.year, context.series, context.edition]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                <p>{context.statement}</p>
              </section>
            ) : null}
          </div>
          <section>
            <h2>On-chain record.</h2>
            <p>
              Public indexer data, checked now. Parent–child links establish
              inscription provenance, not the identity of the artist or
              copyright.
            </p>
            <dl>
              {[
                ["Inscription ID", passport.id],
                ["Reveal transaction", passport.id.split("i")[0]],
                ["Genesis block", passport.genesisHeight],
                [
                  "Inscribed",
                  passport.timestamp
                    ? new Date(passport.timestamp * 1000).toLocaleString(
                        "en-GB",
                      )
                    : null,
                ],
                ["Content", passport.contentType],
                [
                  "File size",
                  typeof passport.contentLength !== "number"
                    ? null
                    : `${passport.contentLength.toLocaleString()} bytes`,
                ],
                ["Current satpoint", passport.satpoint],
                ["Current address", passport.address],
                ["Children", passport.childCount],
              ].map(([label, value]) => (
                <div key={String(label)}>
                  <dt>{label}</dt>
                  <dd>
                    {value === null || value === "" ? "Not reported" : value}
                  </dd>
                </div>
              ))}
            </dl>
            <h3>Parent inscriptions</h3>
            {passport.parents.length ? (
              passport.parents.map((id) => (
                <Link
                  className="ar-record-link"
                  key={id}
                  to={`/artworks/${id}`}
                >
                  {id}
                </Link>
              ))
            ) : (
              <p>No parents reported.</p>
            )}
            {passport.delegate ? (
              <>
                <h3>Delegate</h3>
                <Link
                  className="ar-record-link"
                  to={`/artworks/${passport.delegate}`}
                >
                  {passport.delegate}
                </Link>
              </>
            ) : null}
            {passport.children.length ? (
              <>
                <h3>Children (first {passport.children.length})</h3>
                {passport.children.map((id) => (
                  <Link
                    className="ar-record-link"
                    key={id}
                    to={`/artworks/${id}`}
                  >
                    {id}
                  </Link>
                ))}
              </>
            ) : null}
            <details>
              <summary>On-chain properties</summary>
              <pre>{JSON.stringify(passport.properties, null, 2)}</pre>
            </details>
            <a
              className="ar-text-link"
              href={`https://ordinals.com/inscription/${inscriptionId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Verify independently on Ordinals <ArrowRight />
            </a>
            <p className="ar-passport-disclaimer">
              An edition label is not an enforced supply cap. Current
              possession, a profile and an artist statement do not by themselves
              prove authorship. This passport is an index view, not an on-chain
              certificate.
            </p>
          </section>
        </div>
      ) : null}
      {view ? (
        <GalleryViewer
          works={[context || { ...blankWork(), inscriptionId, title }]}
          onClose={() => setView(false)}
        />
      ) : null}
    </main>
  );
}

function useInbox() {
  const { authorization } = useAppState();
  const activeToken = useRef<string | null>(authorization);
  const dataToken = useRef<string | null>(null);
  const [data, setData] = useState<ArtInbox | null>(null);
  const [error, setError] = useState("");
  const refresh = useCallback(async () => {
    if (!authorization) return;
    try {
      const inbox = await artefactApi<ArtInbox>("/inbox", authorization);
      if (activeToken.current !== authorization) return;
      dataToken.current = authorization;
      setData(inbox);
      setError("");
    } catch (e) {
      if (activeToken.current === authorization) setError(failure(e));
    }
  }, [authorization]);
  useEffect(() => {
    activeToken.current = authorization;
    setData(null);
    setError("");
    void refresh();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, 30000);
    return () => {
      activeToken.current = null;
      window.clearInterval(timer);
    };
  }, [refresh, authorization]);
  return {
    data: dataToken.current === authorization ? data : null,
    error,
    refresh,
  };
}
function FollowArtist({ username }: { username: string }) {
  const { authorization, authorizeWallet, setWalletModalOpen, wallet } =
    useAppState();
  const { data, refresh } = useInbox();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const following =
    data?.follows.some((n) => n.toLowerCase() === username.toLowerCase()) ??
    false;
  return (
    <div>
      <button
        className="ar-follow"
        disabled={busy}
        onClick={() => {
          void (async () => {
            if (!wallet) return setWalletModalOpen(true);
            setBusy(true);
            try {
              const token = authorization || (await authorizeWallet(true));
              if (!token) throw new Error("Verify your wallet");
              await artefactApi("/follow", token, {
                kind: "creator",
                target: username,
                enabled: !following,
              });
              await refresh();
            } catch (e) {
              setError(failure(e));
            } finally {
              setBusy(false);
            }
          })();
        }}
      >
        <Bell />
        {following ? "Following artist" : "Follow artist"}
      </button>
      <ErrorMessage text={error} />
    </div>
  );
}

export function AlertLink() {
  const { data } = useInbox();
  const unread = data?.notices.filter((n) => !n.read).length ?? 0;
  return (
    <Link
      to="/alerts"
      aria-label={unread ? `Alerts, ${unread} unread` : "Alerts"}
    >
      Alerts{unread ? <span className="ar-alert-count">{unread}</span> : null}
    </Link>
  );
}

function WatchAuction({ auction }: { auction: AuctionRecord }) {
  const { authorization, authorizeWallet, setWalletModalOpen, wallet } =
    useAppState();
  const { data, error: loadError, refresh } = useInbox();
  const [target, setTarget] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const watch = data?.watches.find((w) => w.auctionId === auction.id);
  async function save(enabled: boolean) {
    if (!wallet) return setWalletModalOpen(true);
    setBusy(true);
    setError("");
    try {
      const token = authorization || (await authorizeWallet(true));
      if (!token) throw new Error("Verify your wallet");
      await artefactApi("/watch", token, {
        auctionId: auction.id,
        enabled,
        targetSats: !enabled
          ? null
          : target === null
            ? (watch?.targetSats ?? null)
            : target
              ? Number(target)
              : null,
      });
      await refresh();
    } catch (e) {
      setError(failure(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="ar-watch">
      <div>
        <Bell />
        <h3>{watch ? "On your watchlist" : "Keep this one in view."}</h3>
      </div>
      {auction.kind === "dutch" ? (
        <label>
          Target asking price (sats, optional)
          <input
            type="number"
            min={auction.floorPriceSats ?? 1}
            max={auction.startPriceSats}
            step={1}
            placeholder={watch?.targetSats?.toString() || "Notify at a price"}
            value={target ?? watch?.targetSats?.toString() ?? ""}
            onChange={(e) => setTarget(e.target.value)}
          />
        </label>
      ) : null}
      <div className="ar-art-actions">
        <button
          className="ar-secondary"
          disabled={busy}
          onClick={() => void save(true)}
        >
          {watch ? "Update watch" : "Watch auction"}
        </button>
        {watch ? (
          <button disabled={busy} onClick={() => void save(false)}>
            Stop watching
          </button>
        ) : null}
        <Link to="/alerts">Open alerts</Link>
      </div>
      <small>
        In-app opening, outbid and target-price alerts. Refreshes every 30
        seconds while this site is visible. No email, push delivery,
        reservations or automatic purchases.
      </small>
      <ErrorMessage text={error || loadError} />
    </section>
  );
}
export function AlertsPage() {
  const { authorization } = useAppState();
  const { data, error, refresh } = useInbox();
  const [actionError, setActionError] = useState("");
  return (
    <main className="ar-page ar-community">
      <header className="ar-community-heading">
        <div>
          <p className="ar-eyebrow">Your private viewing list</p>
          <h1>
            Stay <em>in the room.</em>
          </h1>
          <p>
            Opening, outbid and Dutch target-price alerts. In-app only,
            refreshed every 30 seconds while visible. No automatic bids or
            purchases.
          </p>
        </div>
        <button className="ar-secondary" onClick={() => void refresh()}>
          Refresh alerts
        </button>
      </header>
      {!authorization ? (
        <Verify />
      ) : (
        <>
          <ErrorMessage text={error || actionError} />
          <div className="ar-inbox">
            {data?.notices.map((n) => (
              <article key={n.id} className={n.read ? "read" : "unread"}>
                <Bell />
                <div>
                  <p className="ar-eyebrow">
                    {n.kind} · {n.read ? "Read" : "New"}
                  </p>
                  <h2>
                    <Link to={`/auctions/${n.auctionId}`}>{n.title}</Link>
                  </h2>
                  <p>{n.message}</p>
                  <div className="ar-art-actions">
                    <Link
                      className="ar-text-link"
                      to={`/auctions/${n.auctionId}`}
                    >
                      View auction <ArrowRight />
                    </Link>
                    {!n.read ? (
                      <button
                        onClick={() => {
                          void (async () => {
                            try {
                              await artefactApi("/read", authorization, {
                                id: n.id,
                              });
                              await refresh();
                            } catch (e) {
                              setActionError(failure(e));
                            }
                          })();
                        }}
                      >
                        Mark read
                      </button>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </div>
          {data && !data.notices.length ? (
            <div className="ar-community-empty">
              <h2>Room for your next discovery.</h2>
              <p>
                Follow a published artist archive or watch an auction to see
                updates here. Signed outbid authorizations are detected
                automatically.
              </p>
              <Link to="/exhibitions">Discover artists</Link>
            </div>
          ) : null}
          <section className="ar-preferences">
            <h2>Following ({data?.follows.length ?? 0})</h2>
            {data?.follows.map((name) => (
              <div key={name}>
                <span>@{name}</span>
                <button
                  onClick={() => {
                    void (async () => {
                      try {
                        await artefactApi("/follow", authorization, {
                          kind: "creator",
                          target: name,
                          enabled: false,
                        });
                        await refresh();
                      } catch (e) {
                        setActionError(failure(e));
                      }
                    })();
                  }}
                >
                  Unfollow
                </button>
              </div>
            ))}
            <h2>Watched auctions ({data?.watches.length ?? 0})</h2>
            {data?.watches.map((w) => (
              <div key={w.auctionId}>
                <Link to={`/auctions/${w.auctionId}`}>
                  {w.auctionId.slice(0, 8)}
                  {w.targetSats
                    ? ` · target ${w.targetSats.toLocaleString()} sats`
                    : ""}
                </Link>
                <button
                  onClick={() => {
                    void (async () => {
                      try {
                        await artefactApi("/watch", authorization, {
                          auctionId: w.auctionId,
                          targetSats: null,
                          enabled: false,
                        });
                        await refresh();
                      } catch (e) {
                        setActionError(failure(e));
                      }
                    })();
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
            <p>
              These preferences are private to your verified account. Delivery
              is not guaranteed while away; always check live terms before
              signing.
            </p>
          </section>
        </>
      )}
    </main>
  );
}
